import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   asNumberOrNull,
   asString,
   formatCurrency,
   formatDate,
   formatDateTime
} from '../utils/report-formatting.utils';
import { parseReportJson } from '../utils/report-json.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

const HIDDEN_INDICATOR_NAMES = new Set<string>(['COMMENT', 'TRACE']);

type OpenFolioRow = {
   rowId: string;
   roomNumber: string;
   name: string;

   arrival: string;
   arrivalRaw: string;

   departure: string;
   departureRaw: string;

   daysOpen: number | '';
   daysOpenRaw: number | null;

   confirmationNumber: string;

   balance: string;
   balanceRaw: number;

   lastModified: string;
   lastModifiedRaw: string;

   indicators: string;
   reservationIndicators: unknown[];

   reservationId: string;
   roomClass: string;
   pseudoRoom: boolean;
   commentCount: number;
   traceCount: number;

   hasRealTyallsRowId: boolean;
};

function getRowId(row: any): string {
   const realTyallsRowId = getRealTyallsRowId(row);

   if (realTyallsRowId) {
      return realTyallsRowId;
   }

   return (
      asString(row?.Id ?? row?.id) ||
      `open-folio:${asString(row?.RoomNumber)}:${asString(
         row?.ConfirmationNumber
      )}:${asString(row?.Name)}`
   );
}

function getSourceRows(reportJson: unknown): any[] {
   const parsedReportJson = parseReportJson(reportJson);

   const openFolioRows = parsedReportJson['OpenFolios'];

   if (Array.isArray(openFolioRows)) {
      return openFolioRows;
   }

   const upperCaseRows = parsedReportJson['Rows'];

   if (Array.isArray(upperCaseRows)) {
      return upperCaseRows;
   }

   const lowerCaseRows = parsedReportJson['rows'];

   if (Array.isArray(lowerCaseRows)) {
      return lowerCaseRows;
   }

   return [];
}

function getBoolean(value: unknown): boolean {
   if (typeof value === 'boolean') {
      return value;
   }

   return asString(value).trim().toLowerCase() === 'true';
}

function getReservationIndicators(row: any): unknown[] {
   const indicators = row?.ReservationIndicators ?? row?.reservationIndicators;

   return Array.isArray(indicators) ? indicators : [];
}

function getIndicatorName(indicator: any): string {
   return asString(indicator?.indicatorName ?? indicator?.IndicatorName)
      .trim()
      .toUpperCase();
}

function formatDisplayedIndicators(indicators: unknown[]): string {
   const displayedIndicatorNames: string[] = [];
   const seenIndicatorNames = new Set<string>();

   for (const indicator of indicators) {
      const indicatorName = getIndicatorName(indicator);

      if (
         !indicatorName ||
         HIDDEN_INDICATOR_NAMES.has(indicatorName) ||
         seenIndicatorNames.has(indicatorName)
      ) {
         continue;
      }

      seenIndicatorNames.add(indicatorName);
      displayedIndicatorNames.push(indicatorName);
   }

   return displayedIndicatorNames.join(', ');
}

function normaliseRows(reportJson: unknown): OpenFolioRow[] {
   return getSourceRows(reportJson)
      .map((row): OpenFolioRow => {
         const balanceRaw =
            asNumberOrNull(row?.Balance, {
               removeGroupingSeparators: true
            }) ?? 0;

         const arrivalRaw = asString(row?.ArrivalDate);
         const departureRaw = asString(row?.DepartureDate);

         const daysOpenRaw = asNumberOrNull(row?.DaysOpen);
         const daysOpen: number | '' = daysOpenRaw === null ? '' : daysOpenRaw;

         const lastModifiedRaw = asString(row?.LastModifyDateTime);

         const reservationIndicators = getReservationIndicators(row);

         return {
            rowId: getRowId(row),

            roomNumber: asString(row?.RoomNumber),
            name: asString(row?.Name),

            arrival: formatDate(arrivalRaw),
            arrivalRaw,

            departure: formatDate(departureRaw),
            departureRaw,

            daysOpen,
            daysOpenRaw,

            confirmationNumber: asString(row?.ConfirmationNumber),

            balance: formatCurrency(balanceRaw),
            balanceRaw,

            lastModified: formatDateTime(lastModifiedRaw),
            lastModifiedRaw,

            indicators: formatDisplayedIndicators(reservationIndicators),
            reservationIndicators,

            reservationId: asString(row?.ReservationId),
            roomClass: asString(row?.RoomClass),
            pseudoRoom: getBoolean(row?.PseudoRoom),

            commentCount: asNumberOrNull(row?.CommentCount) ?? 0,

            traceCount: asNumberOrNull(row?.TraceCount) ?? 0,

            hasRealTyallsRowId: !!getRealTyallsRowId(row)
         };
      })
      .filter((row) => row.roomNumber || row.name || row.confirmationNumber);
}

function sortRows(rows: OpenFolioRow[]): OpenFolioRow[] {
   return [...rows].sort((a, b) => {
      const aRoom = Number(a.roomNumber);
      const bRoom = Number(b.roomNumber);

      if (Number.isFinite(aRoom) && Number.isFinite(bRoom)) {
         return aRoom - bRoom;
      }

      return a.roomNumber.localeCompare(b.roomNumber);
   });
}

export const OPEN_FOLIOS_DEFINITION: ReportDefinition = {
   reportType: 'OpenFolios',
   version: 3,
   title: 'Open Folios',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Open Folios',
      snippets: ({ allRows }) => {
         const dataRows = allRows.filter(
            (row) => row.cells?.['rowType'] === 'openFolio'
         );

         const totalBalance = dataRows.reduce((total, row) => {
            const amount = Number(row.cells?.['balanceRaw']);

            return Number.isFinite(amount) ? total + amount : total;
         }, 0);

         return [
            {
               label: 'Count',
               value: dataRows.length
            },
            {
               label: 'Total Balance',
               value: formatCurrency(totalBalance),
               tone: totalBalance < 0 ? 'danger' : 'neutral'
            }
         ];
      }
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['openFolio']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['openFolio'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   tableMinWidth: '1460px',

   columns: [
      {
         key: 'roomNumber',
         header: 'ROOM',
         width: '90px',
         sort: {
            type: 'string'
         }
      },
      {
         key: 'name',
         header: 'NAME',
         width: '260px',
         maxChars: 100,
         sort: {
            type: 'string'
         }
      },
      {
         key: 'arrival',
         header: 'ARRIVAL',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['arrivalRaw']
         }
      },
      {
         key: 'departure',
         header: 'DEPARTURE',
         width: '120px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['departureRaw']
         }
      },
      {
         key: 'daysOpen',
         header: 'DAYS OPEN',
         width: '100px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['daysOpenRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'confirmationNumber',
         header: 'CONFIRMATION',
         width: '160px',
         sort: {
            type: 'string'
         }
      },
      {
         key: 'balance',
         header: 'BALANCE',
         width: '130px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['balanceRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'lastModified',
         header: 'LAST MODIFIED',
         width: '190px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['lastModifiedRaw']
         }
      },
      {
         key: 'indicators',
         header: 'INDICATORS',
         width: '260px',
         maxChars: 100,
         sort: {
            type: 'string'
         }
      }
   ],

   filter: {
      fields: [
         {
            key: 'roomNumber',
            label: 'Room',
            type: 'string'
         },
         {
            key: 'name',
            label: 'Name',
            type: 'string'
         },
         {
            key: 'arrival',
            label: 'Arrival',
            type: 'date',
            getValue: (row) => row.cells['arrivalRaw']
         },
         {
            key: 'departure',
            label: 'Departure',
            type: 'date',
            getValue: (row) => row.cells['departureRaw']
         },
         {
            key: 'daysOpen',
            label: 'Days Open',
            type: 'number',
            getValue: (row) => row.cells['daysOpenRaw']
         },
         {
            key: 'confirmationNumber',
            label: 'Confirmation Number',
            type: 'string'
         },
         {
            key: 'balance',
            label: 'Balance',
            type: 'number',
            getValue: (row) => row.cells['balanceRaw']
         },
         {
            key: 'lastModified',
            label: 'Last Modified',
            type: 'date',
            getValue: (row) => row.cells['lastModifiedRaw']
         },
         {
            key: 'indicators',
            label: 'Indicators',
            type: 'string'
         }
      ]
   },

   flattenRows: (reportJson: unknown): ReportRowVm[] => {
      const rows = sortRows(normaliseRows(reportJson));

      return rows.map((row) => ({
         rowId: row.rowId,
         isAnnotatable: row.hasRealTyallsRowId,

         cells: {
            rowType: 'openFolio',

            roomNumber: row.roomNumber,
            name: row.name,

            arrival: row.arrival,
            arrivalRaw: row.arrivalRaw,

            departure: row.departure,
            departureRaw: row.departureRaw,

            daysOpen: row.daysOpen,
            daysOpenRaw: row.daysOpenRaw,

            confirmationNumber: row.confirmationNumber,

            balance: row.balance,
            balanceRaw: row.balanceRaw,

            lastModified: row.lastModified,
            lastModifiedRaw: row.lastModifiedRaw,

            indicators: row.indicators,

            reservationId: row.reservationId,
            roomClass: row.roomClass,
            pseudoRoom: row.pseudoRoom,

            commentCount: row.commentCount,
            traceCount: row.traceCount,

            reservationIndicators: row.reservationIndicators
         }
      }));
   }
};
